import { createContext, useContext, useEffect, useState, useCallback } from "react";

const CartContext = createContext(null);
const CART_KEY = "candle_co_cart";
const WISH_KEY = "candle_co_wishlist";
const COUPON_KEY = "candle_co_coupon";

const load = (k, fallback) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? fallback; }
  catch { return fallback; }
};

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => load(CART_KEY, []));
  const [wishlist, setWishlist] = useState(() => load(WISH_KEY, []));
  const [coupon, setCoupon] = useState(() => load(COUPON_KEY, null));
  const [cartAnimTick, setCartAnimTick] = useState(0);

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem(WISH_KEY, JSON.stringify(wishlist)); }, [wishlist]);
  useEffect(() => { localStorage.setItem(COUPON_KEY, JSON.stringify(coupon)); }, [coupon]);

  const _keyFor = (productId, variant) =>
    `${productId}::${variant ? JSON.stringify(variant) : "default"}`;

  const addToCart = useCallback((product, variant = null, quantity = 1) => {
    setItems((prev) => {
      const key = _keyFor(product.id, variant);
      const idx = prev.findIndex((it) => _keyFor(it.productId, it.variant) === key);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      const unitPrice = product.price + (variant ? Object.values(variant).reduce((s, v) => s + (v.priceDelta || 0), 0) : 0);
      return [...prev, {
        productId: product.id,
        name: product.name,
        image: product.image,
        scent: product.scent,
        variant,
        quantity,
        unitPrice,
      }];
    });
    setCartAnimTick((t) => t + 1);
  }, []);

  const updateQty = (idx, delta) => {
    setItems((prev) => prev.map((it, i) => i === idx
      ? { ...it, quantity: Math.max(1, it.quantity + delta) }
      : it));
  };

  const removeItem = (idx) => setItems((prev) => prev.filter((_, i) => i !== idx));
  const clearCart = () => { setItems([]); setCoupon(null); };

  const toggleWishlist = (productId) => {
    setWishlist((prev) => prev.includes(productId)
      ? prev.filter((id) => id !== productId)
      : [...prev, productId]);
  };

  const subtotal = items.reduce((s, it) => s + it.unitPrice * it.quantity, 0);
  const itemCount = items.reduce((s, it) => s + it.quantity, 0);

  return (
    <CartContext.Provider value={{
      items, wishlist, coupon, setCoupon,
      addToCart, updateQty, removeItem, clearCart, toggleWishlist,
      subtotal, itemCount, cartAnimTick,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
